import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  afterNextRender,
  booleanAttribute,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { InputMaskModule, InputmaskOptions } from '@ngneat/input-mask';
import clsx from 'clsx';
import { IconInfoComponent } from '@pedix-workspace/angular-ui-icons';
import {
  WithDefaultValidatorProps,
  composeValidatorFn,
  hasChangedAnyDefaultValidatorProps,
} from '../utils/defaultValidator';
import { Observable, Subject, debounceTime } from 'rxjs';
import { UiFormReactiveConfigService } from '../form-reactive-config.service';

let INPUT_UID = 0;

export type InputTextLabelSize = 'xs' | 'sm' | 'md';

export type InputTextValueType = string | undefined | null;

export const inputModes = [
  'none',
  'text',
  'decimal',
  'numeric',
  'tel',
  'search',
  'email',
  'url',
] as const;
export type InputModes = typeof inputModes;
export type InputMode = InputModes[number];

@Component({
  selector: 'pxw-input-text',
  templateUrl: './input-text.component.html',
  styleUrl: './input-text.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: InputTextComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: InputTextComponent,
    },
  ],
  imports: [NgClass, IconInfoComponent, InputMaskModule],
})
export class InputTextComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator, WithDefaultValidatorProps
{
  private cd = inject(ChangeDetectorRef);

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  @Input({ required: true }) name: string;
  @Input() type: 'text' | 'password' = 'text';
  @Input() label: string | undefined;
  @Input() labelSize: InputTextLabelSize = 'sm';
  @Input() helper: string | undefined;
  @Input() placeholder: string | undefined;
  @Input() autofocus: boolean;
  /**
   * Enfoca al renderizar y deja el valor preseleccionado, para escribir encima sin borrar. A diferencia
   * de `autofocus` —que es el atributo nativo y sólo actúa al parsear el HTML— funciona en un modal o
   * un panel que se monta después.
   */
  @Input({ transform: booleanAttribute }) autoselect = false;
  @Input() messages: { [key: string]: string } = {};
  @Input() displayErrors: boolean | null | undefined = undefined;
  @Input() debounceTime = 0;
  @Input() inputMode: InputMode = 'text';
  readonly mask = input<InputmaskOptions<string>>();

  @Input({ transform: booleanAttribute }) required?: boolean;
  @Input({ transform: booleanAttribute }) requiredTrue?: boolean;
  @Input({ transform: booleanAttribute }) email?: boolean;
  @Input() minLength?: number;
  @Input() maxLength?: number;
  @Input() pattern?: RegExp | readonly RegExp[];
  @Input() custom?: ValidatorFn;

  @Output() keyUp = new EventEmitter<KeyboardEvent>();
  @Output() focusOut = new EventEmitter<void>();

  private injector = inject(Injector);

  protected inputId: string;
  protected isDisabled: boolean;

  #value = signal<InputTextValueType>(undefined);
  protected readonly renderedValue = signal<InputTextValueType>(undefined);

  private onChange: (value: InputTextValueType) => void;
  private onTouched: () => void;
  private onValidatorChange: () => void;
  private validatorFn: ValidatorFn | null;
  private formReactiveConfigService = inject(UiFormReactiveConfigService);

  private inputChanges: Subject<InputTextValueType>;

  inputChanges$: Observable<InputTextValueType>;

  @HostBinding('class') get classNames() {
    return clsx('ui-input', `ui-input-text`, {
      'ui-input--disabled': this.isDisabled,
      'ui-input--with-helper': this.helper,
      'ui-input--with-errors': !this.isValid,
    });
  }

  get value(): InputTextValueType {
    return this.#value();
  }

  get patternSource(): string | undefined {
    return this.pattern instanceof RegExp ? this.pattern.source : undefined;
  }

  get formControl() {
    const ngControl = this.injector.get(NgControl, null);

    if (ngControl) {
      return ngControl.control as FormControl;
    }
    return null;
  }

  get isRequired() {
    return this.formControl?.hasValidator(Validators.required) || this.required === true;
  }

  get isValid() {
    return this.formControl?.valid;
  }

  get isTouched() {
    return this.formControl?.touched;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get errorEntries(): [string, any][] {
    return Object.entries(this.formControl?.errors || {});
  }

  get shouldDisplayErrors() {
    if (this.displayErrors === false) {
      return false;
    }
    return (this.displayErrors || this.isTouched) && !this.isValid && !this.isDisabled;
  }

  constructor() {
    afterNextRender(() => {
      if (!this.autoselect) {
        return;
      }

      const inputElement = this.input.nativeElement;

      // El `setTimeout` NO es cosmético: cuando el panel se abre desde un click, el `mouseup` del
      // browser termina DESPUÉS de este render y colapsa cualquier selección hecha en el mismo turno.
      // Seleccionar en el siguiente macrotask es lo único que sobrevive a eso.
      setTimeout(() => {
        inputElement.focus();
        inputElement.select();
      });
    });
  }

  ngOnInit(): void {
    this.inputId = `input-text-${++INPUT_UID}`;

    this.inputChanges = new Subject();
    this.inputChanges$ = this.inputChanges.asObservable().pipe(debounceTime(this.debounceTime));

    this.inputChanges$.subscribe(inputValue => {
      this.onChange(inputValue);
      this.onValidatorChange();

      this.cd.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (hasChangedAnyDefaultValidatorProps(changes)) {
      this.validatorFn = composeValidatorFn(this);

      if (this.formControl) {
        this.formControl.updateValueAndValidity();
      }
    }
  }

  writeValue(value: InputTextValueType) {
    const nextValue = value || null;

    this.#value.set(nextValue);
    this.renderedValue.set(nextValue);

    if (this.input) {
      this.input.nativeElement.value = nextValue ?? '';
    }
  }
  registerOnChange(onChange: (value: InputTextValueType) => void) {
    this.onChange = onChange;
  }
  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validate(control: AbstractControl): ValidationErrors | null {
    return this.validatorFn?.(control) || null;
  }
  registerOnValidatorChange(onValidatorChange: () => void): void {
    this.onValidatorChange = onValidatorChange;
  }

  onInputChange($event: Event) {
    const newValue = (<HTMLInputElement>$event.target).value;

    if (newValue === (this.value ?? '')) {
      return;
    }
    this.#value.set(newValue);
    this.onTouched();

    this.inputChanges.next(newValue);
  }

  onKeyUp($event: KeyboardEvent) {
    const newValue = (<HTMLInputElement>$event.target).value;

    this.#value.set(newValue);
    this.onTouched();

    this.inputChanges.next(newValue);

    this.keyUp.emit($event);
  }

  /**
   * `select: true` deja además el valor preseleccionado, para escribir encima sin borrar.
   */
  focus({ select = false }: { select?: boolean } = {}) {
    if (select) {
      this.input.nativeElement.select();
      return;
    }
    this.input.nativeElement.focus();
  }

  onFocusOut() {
    this.focusOut.emit();

    if (this.value && this.value !== this.value.trim()) {
      const trimmedValue = this.value.trim();

      this.writeValue(trimmedValue);

      this.inputChanges.next(trimmedValue);
    }
  }

  onPaste($event: ClipboardEvent) {
    $event.preventDefault();
    $event.stopImmediatePropagation();

    const target = <HTMLInputElement>$event.target;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pastedText = ($event.clipboardData || (window as any)['clipboardData']).getData('text');
    const start = target.selectionStart || 0;
    const end = target.selectionEnd || 0;
    const currentValue = target.value;

    const newValue = currentValue.slice(0, start) + pastedText + currentValue.slice(end);

    this.writeValue(newValue);
    this.onTouched();

    this.inputChanges.next(newValue);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected getErrorMessage([key, data]: [string, any]) {
    return this.messages?.[key] || this.formReactiveConfigService.getErrorMessage(key, data);
  }
}
